import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';

const EstaduaisPage = () => {
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const estaduais = [
    {
      name: 'Paulistão',
      teams: 16,
      level: 'A+',
      zebras: 34,
      avgOdds: 4.2,
      city: 'São Paulo',
      tip: 'Aposte contra favoritos em derbies',
      region: 'sudeste',
      revelations: 89
    },
    {
      name: 'Cariocão',
      teams: 12,
      level: 'A',
      zebras: 38,
      avgOdds: 4.5,
      city: 'Rio de Janeiro',
      tip: 'Fla-Flu sempre Over 2.5 gols',
      region: 'sudeste',
      revelations: 76
    },
    {
      name: 'Mineirão',
      teams: 12,
      level: 'A',
      zebras: 31,
      avgOdds: 3.9,
      city: 'Belo Horizonte',
      tip: 'Under gols em clássicos',
      region: 'sudeste',
      revelations: 62
    },
    {
      name: 'Gauchão',
      teams: 12,
      level: 'A',
      zebras: 29,
      avgOdds: 3.7,
      city: 'Porto Alegre',
      tip: 'Under 2.5 é quase garantia',
      region: 'sul',
      revelations: 58
    },
    {
      name: 'Baianão',
      teams: 10,
      level: 'B+',
      zebras: 42,
      avgOdds: 5.1,
      city: 'Salvador',
      tip: 'Times do interior em casa',
      region: 'nordeste',
      revelations: 45
    },
    {
      name: 'Pernambucano',
      teams: 10,
      level: 'B+',
      zebras: 45,
      avgOdds: 5.3,
      city: 'Recife',
      tip: 'Aposte em zebras até maio',
      region: 'nordeste',
      revelations: 41
    },
    {
      name: 'Cearense',
      teams: 10,
      level: 'B',
      zebras: 47,
      avgOdds: 5.5,
      city: 'Fortaleza',
      tip: 'Clássico-Rei sempre cartões',
      region: 'nordeste',
      revelations: 38
    },
    {
      name: 'Paranaense',
      teams: 12,
      level: 'B+',
      zebras: 36,
      avgOdds: 4.1,
      city: 'Curitiba',
      tip: 'Athletiba é garantia de gols',
      region: 'sul',
      revelations: 42
    },
    {
      name: 'Catarinense',
      teams: 12,
      level: 'B',
      zebras: 41,
      avgOdds: 4.8,
      city: 'Florianópolis',
      tip: 'Janeiro zebras garantidas',
      region: 'sul',
      revelations: 35
    }
  ];

  const seasonalPatterns = [
    {
      month: 'Janeiro',
      phase: 'INÍCIO EXPERIMENTAL',
      zebraRate: 52,
      strategy: 'Aposte contra grandes com reservas'
    },
    {
      month: 'Fevereiro',
      phase: 'ADAPTAÇÃO',
      zebraRate: 44,
      strategy: 'Foque em times do interior casa'
    },
    {
      month: 'Março',
      phase: 'INTENSIFICAÇÃO',
      zebraRate: 35,
      strategy: 'Value bets em azarões motivados'
    },
    {
      month: 'Abril',
      phase: 'DECISIVO',
      zebraRate: 28,
      strategy: 'Under gols, Over cartões'
    },
    {
      month: 'Maio',
      phase: 'CONCLUSÃO',
      zebraRate: 22,
      strategy: 'Mercados alternativos'
    }
  ];

  const culturalDNA = [
    {
      region: 'São Paulo',
      pressure: 'EXTREMA',
      pattern: 'Grandes sofrem com expectativa',
      zebraRate: 34,
      characteristics: ['Alta técnica', 'Pressão intensa', 'Mídia pesada']
    },
    {
      region: 'Rio de Janeiro',
      pressure: 'MODERADA',
      pattern: 'Malandragem funciona',
      zebraRate: 38,
      characteristics: ['Jogo bonito', 'Imprevisível', 'Talento individual']
    },
    {
      region: 'Minas Gerais',
      pressure: 'EQUILIBRADA',
      pattern: 'Organização prevalece',
      zebraRate: 31,
      characteristics: ['Tático', 'Conservador', 'Eficiente']
    },
    {
      region: 'Rio Grande do Sul',
      pressure: 'INTENSA',
      pattern: 'Frieza em decisões',
      zebraRate: 29,
      characteristics: ['Físico', 'Guerreiro', 'Decisivo']
    },
    {
      region: 'Nordeste',
      pressure: 'EMOCIONAL',
      pattern: 'Paixão supera razão',
      zebraRate: 44,
      characteristics: ['Calor', 'Torcida', 'Imprevisível']
    }
  ];

  const faqs = [
    {
      question: 'Por que estaduais são imprevisíveis no início?',
      answer: 'Janeiro é mês de experimentos: times testam jovens, técnicos novos implementam sistemas, forma física irregular. 52% das zebras anuais acontecem nos primeiros dois meses.'
    },
    {
      question: 'Como identificar uma revelação?',
      answer: 'Monitore jovens de 16-19 anos ganhando titularidade, números acima da média, convocações de base e visitas de olheiros europeus.'
    },
    {
      question: 'Qual estadual oferece melhores oportunidades?',
      answer: 'Janeiro-Março: Paulistão e Cariocão (zebras). Março-Abril: Nordestinos (mando de campo). Maio: todos (finais imprevisíveis).'
    },
    {
      question: 'Times grandes "entregam" jogos?',
      answer: 'Não propositalmente, mas priorizam diferente: testam reservas, poupam titulares, focam no Brasileirão/Libertadores.'
    }
  ];

  const REGION_LABELS = {
    all: 'Todos',
    sudeste: 'Sudeste',
    sul: 'Sul',
    nordeste: 'Nordeste'
  };

  const filteredEstaduais = estaduais.filter(estadual => {
    const matchesRegion = selectedRegion === 'all' || estadual.region === selectedRegion;
    const matchesSearch = estadual.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          estadual.city.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRegion && matchesSearch;
  });

  const regionCount = estaduais.reduce((acc, estadual) => {
    acc[estadual.region] = (acc[estadual.region] || 0) + 1;
    return acc;
  }, {});

  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Campeonatos Estaduais 2024: Guia Completo de Apostas",
    "description": "Análise completa dos estaduais brasileiros 2024. Paulistão, Cariocão, Mineirão e mais. Padrões sazonais e culturais.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/estaduais"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Campeonatos Estaduais 2024 | Paulistão, Cariocão, Mineirão e Mais"
        description="Guia completo dos Estaduais 2024. Análise de Paulistão, Cariocão, Mineirão, Gauchão. Padrões sazonais, revelações e estratégias de apostas com IA."
        keywords="campeonatos estaduais 2024, paulistão, cariocão, mineirão, gauchão, baianão, pernambucano, estaduais apostas, futebol brasileiro"
        canonical="https://pickstarai.com/estaduais"
        ogImage="https://pickstarai.com/og-estaduais.jpg"
        structuredData={[articleStructuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Estaduais 2024
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                Laboratório do futebol: onde estrelas nascem e tradições se renovam
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar estaduais..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/10 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 pb-12 border-b border-white/[0.05]">
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">27</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ESTADUAIS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">78%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">REVELAÇÕES</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">52%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ZEBRAS JAN</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">PRO</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">PRECISÃO</div>
            </div>
          </div>

          {/* Region Filter */}
          <div className="mb-12">
            <div className="flex gap-2 overflow-x-auto">
              {Object.entries(REGION_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedRegion(key)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all whitespace-nowrap ${
                    selectedRegion === key
                      ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10'
                  }`}
                >
                  {label} {key !== 'all' && `(${regionCount[key] || 0})`}
                </button>
              ))}
            </div>
          </div>

          {/* Main Estaduais */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">COMPETIÇÕES</span>
              PRINCIPAIS ESTADUAIS
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEstaduais.map((estadual, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-white/90">{estadual.name}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{estadual.city}</p>
                    </div>
                    <span className="text-xs font-mono text-emerald-400/80 uppercase tracking-wider">
                      {estadual.level}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-white/[0.05]">
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Times</div>
                      <div className="text-lg font-mono text-white/80">{estadual.teams}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Zebras</div>
                      <div className="text-lg font-mono text-white/80">{estadual.zebras}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Odd Média</div>
                      <div className="text-lg font-mono text-white/80">@{estadual.avgOdds}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Revelações</div>
                      <div className="text-lg font-mono text-emerald-400/80">{estadual.revelations}</div>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Dica: </span>
                    <span className="text-xs text-white/60">{estadual.tip}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Seasonal Evolution */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">CALENDÁRIO</span>
              EVOLUÇÃO SAZONAL: JANEIRO A MAIO
            </h2>

            <div className="space-y-4">
              {seasonalPatterns.map((pattern, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em]">{pattern.month}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mt-1">{pattern.phase}</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-mono text-emerald-400/80">{pattern.zebraRate}%</div>
                      <div className="text-[10px] text-white/30 uppercase tracking-wider">ZEBRAS</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/50 mb-3">{pattern.strategy}</p>
                  <div className="w-full bg-white/[0.05] h-1">
                    <div
                      className="h-1 bg-gradient-to-r from-emerald-500/80 to-violet-500/80"
                      style={{ width: `${pattern.zebraRate}%` }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Cultural DNA */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ANÁLISE</span>
              DNA CULTURAL REGIONAL
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {culturalDNA.map((culture, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-white/90">{culture.region}</h3>
                    <span className="text-xs font-mono text-white/60 uppercase tracking-wider">
                      {culture.pressure}
                    </span>
                  </div>

                  <p className="text-sm text-white/50 mb-4">{culture.pattern}</p>

                  <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-white/[0.05]">
                    {culture.characteristics.map((char, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-white/[0.05] text-white/60 uppercase tracking-wider">
                        {char}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/40 text-xs uppercase tracking-wider">Taxa de Zebras:</span>
                    <span className="font-mono text-emerald-400/80">{culture.zebraRate}%</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              DESCUBRA A PRÓXIMA REVELAÇÃO
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Nossa IA já identificou 347 talentos antes de virarem estrelas. Domine os segredos regionais do futebol brasileiro.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              SCOUT DIGITAL GRATUITO
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default EstaduaisPage;
